import { QueryClient, VueQueryPlugin, dehydrate, hydrate } from '@tanstack/vue-query'
import { defineNuxtPlugin, useState } from 'nuxt/app'
import type { DehydratedState, VueQueryPluginOptions } from '@tanstack/vue-query'

/**
 * Server only: stop every query of this request's client from arming a gc timer.
 *
 * A finite gcTime (the default below, or a per-query override) arms a setTimeout whenever a query
 * loses its last observer. After the render Nuxt disposes the app, every useQuery drops its
 * observer, and each timer then keeps the whole request in memory for that long. Infinity is
 * TanStack's own server default; Query only ever raises gcTime, so later options cannot lower it.
 */
export function disableServerGcTimers(queryClient: QueryClient): void {
  queryClient.getQueryCache().subscribe(event => {
    if (event.type === 'added') {
      event.query.gcTime = Infinity
    }
  })
}

/**
 * SSR request teardown: snapshot the cache for the client payload, then drop it. clear() removes
 * every query, which cancels in-flight fetches and any gc timer armed before disableServerGcTimers
 * took effect (a query schedules one in its constructor).
 */
export function dehydrateAndRelease(queryClient: QueryClient): DehydratedState {
  const state = dehydrate(queryClient)
  queryClient.clear()
  return state
}

/**
 * Vue Query plugin - shared across all apps
 * Sets up TanStack Query with SSR hydration support
 */
export default defineNuxtPlugin(nuxt => {
  const vueQueryState = useState<DehydratedState | null>('vue-query')

  // Create a new QueryClient instance for each request
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        // SSR retries run after the request ends (no Nuxt/Pinia context → lost auth).
        // Client keeps one retry for transient network failures.
        retry: import.meta.server ? false : 1,
        refetchOnWindowFocus: false,
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      },
    },
  })

  if (import.meta.server) disableServerGcTimers(queryClient)

  const options: VueQueryPluginOptions = { queryClient }

  nuxt.vueApp.use(VueQueryPlugin, options)

  // SSR hydration
  if (import.meta.server) {
    nuxt.hooks.hook('app:rendered', () => {
      vueQueryState.value = dehydrateAndRelease(queryClient)
    })
  }

  if (import.meta.client) {
    // Hydrate immediately on client to ensure queries don't refetch
    if (vueQueryState.value) {
      hydrate(queryClient, vueQueryState.value)
    }
  }

  // Expose queryClient to nuxtApp for middleware access
  return {
    provide: {
      queryClient,
    },
  }
})
